import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, MessageCircle, Loader2, Settings, TrendingUp, Bell, Bot
} from 'lucide-react';
import { API_URL } from '../../config';
import { SalesPanel, ConversationsPanel, TelegramPanel } from './PagePanels';
import BotConfigPanel from '../shared/BotConfigPanel';
import { RequestServiceCard, PendingRequestCard } from '../shared/ServiceRequestForm';

// ============================================================
// WHATSAPP - Module client
// Service géré par un humain (comme Page Facebook) : demande ->
// approbation -> l'admin crée la connexion -> le client la gère ici
// (Ventes, Conversations, Alertes Telegram, Config de l'agent).
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function WhatsAppConnect({ onBack }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [panel, setPanel] = useState(null); // { clientId, tab }

  const loadConnections = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/my-connections`, { headers: authHeaders() });
      const data = await res.json();
      // Cet ecran est dedie au WhatsApp : les connexions Messenger (channel='facebook')
      // sont affichees separement sur l'ecran Page Facebook.
      if (data.success) setConnections(data.connections.filter(c => c.channel !== 'facebook'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadAccessStatus = useCallback(async () => {
    setAccessLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/service-requests/status?service=whatsapp`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAccessStatus(data);
    } catch (e) { console.error(e); }
    finally { setAccessLoading(false); }
  }, []);

  useEffect(() => { loadConnections(); loadAccessStatus(); }, [loadConnections, loadAccessStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-green-950 to-slate-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={onBack} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">WhatsApp</h1>
              <p className="text-xs md:text-sm text-green-200">Votre agent IA WhatsApp, configuré par notre équipe</p>
            </div>
          </div>
        </div>

        {(accessLoading || loading) ? (
          <div className="text-center py-10"><Loader2 className="w-8 h-8 text-green-400 animate-spin mx-auto" /></div>
        ) : connections.length === 0 ? (
          accessStatus?.request_status === 'pending' ? (
            <PendingRequestCard text={
              <>Notre équipe <span className="text-white font-semibold">TDI DIGITAL</span> a bien reçu votre demande
              et vous contacte personnellement très rapidement pour activer votre agent IA WhatsApp.</>
            } />
          ) : (
            <RequestServiceCard
              service="whatsapp"
              title="Un vrai humain configure votre agent WhatsApp"
              description={<>Chez <span className="text-white font-semibold">TDI DIGITAL</span>, votre agent IA WhatsApp
              n'est pas livré tel quel : notre équipe vérifie votre numéro et configure l'agent avec vous, pour garantir
              une vraie qualité de service.</>}
              accessStatus={accessStatus}
              onSent={loadAccessStatus}
              rejected={accessStatus?.request_status === 'rejected'}
            />
          )
        ) : (
          <div className="space-y-4">
            {connections.map(conn => (
              <div key={conn.client_id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-bold">{conn.page_name}</div>
                      <div className="text-green-200/70 text-xs">{conn.whatsapp_number}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`w-2 h-2 rounded-full ${conn.bot_active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
                        <span className={`text-xs ${conn.bot_active ? 'text-green-300' : 'text-gray-400'}`}>
                          {conn.bot_active ? 'Agent IA actif — il répond à vos clients' : 'Agent IA en pause'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Onglets de gestion */}
                <div className="mt-4 flex gap-2 flex-wrap border-t border-white/10 pt-4">
                  {[
                    { tab: 'sales',         icon: TrendingUp,    label: 'Ventes' },
                    { tab: 'conversations', icon: MessageCircle, label: 'Conversations' },
                    { tab: 'telegram',      icon: Bell,          label: 'Alertes Telegram' },
                    ...(conn.self_config_allowed ? [{ tab: 'config', icon: Settings, label: 'Configurer mon agent' }] : []),
                  ].map(({ tab, icon: Icon, label }) => {
                    const isOpen = panel?.clientId === conn.client_id && panel?.tab === tab;
                    return (
                      <button
                        key={tab}
                        onClick={() => setPanel(isOpen ? null : { clientId: conn.client_id, tab })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                          isOpen ? 'bg-green-600 text-white' : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    );
                  })}
                </div>

                {panel?.clientId === conn.client_id && (
                  <div className="mt-4 bg-black/20 rounded-2xl p-4">
                    {panel.tab === 'sales' && <SalesPanel clientId={conn.client_id} onOpenConversation={() => setPanel({ clientId: conn.client_id, tab: 'conversations' })} />}
                    {panel.tab === 'conversations' && <ConversationsPanel clientId={conn.client_id} />}
                    {panel.tab === 'telegram' && <TelegramPanel clientId={conn.client_id} />}
                    {panel.tab === 'config' && <BotConfigPanel clientId={conn.client_id} />}
                  </div>
                )}

                {!conn.has_config && (
                  <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-xs flex items-center gap-2">
                    <Bot className="w-4 h-4 flex-shrink-0" />
                    Notre équipe finalise la configuration de votre agent IA WhatsApp.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
